import {
  Button,
  Card,
  Flex,
  Header,
  InputNumber,
  Text,
  useToast,
} from '@driftt/dds'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DropResult } from 'react-beautiful-dnd'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'

import {
  Attributes,
  CQLRatingGroup,
  EducationPopover,
  LeavingModal,
} from 'components/AIConfiguration/LeadQualification/PersonaCard/components'
import {
  AILQTrackAction,
  useLQTracking,
} from 'components/AIConfiguration/LeadQualification/useLQTracking'
import { useGPTQualificationAttributes } from 'hooks/useGPTQualificationAttributes'
import { PropertyDefinition } from 'types/contacts'

import { PROMPT_MODES, QUALIFICATION_DEFAULT_NAME } from '../constants'
import { PersonaConfig } from '../types'
import ConfirmationModal from './components/ConfirmationModal'
import { buildAttributesPayload, transformInitialValues } from './helpers'
import { PersonaForm } from './types'

interface Props {
  persona?: PersonaConfig
  onPersonaSave?: () => void
  contactAttributes: PropertyDefinition[]
}

const PersonaCard: React.FC<Props> = ({
  persona,
  onPersonaSave,
  contactAttributes,
}) => {
  const toast = useToast()
  const personaContainer = useRef<HTMLFormElement>(null)
  const { trackLQ } = useLQTracking()

  const { handleSavePersona } = useGPTQualificationAttributes({
    onSavePersonaSuccess: onPersonaSave,
  })
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const [leaveModalConfig, setLeaveModalConfig] = useState({
    open: false,
    nextHref: '',
  })
  const [minCriteriaMet, setMinCriteriaMet] = useState<number>(
    () => persona?.minCriteriaMet ?? 1
  )
  const [qualifiedCQL, setQualifiedCQL] = useState<number>(
    () => persona?.qualifiedCQL ?? 0
  )
  const [unqualifiedCQL, setUnqualifiedCQL] = useState<number>(
    () => persona?.unqualifiedCQL ?? 0
  )
  const [showAssistance, setShowAssistance] = useState<boolean>(false)

  const defaultValues = useMemo(
    () =>
      transformInitialValues({
        data: persona,
        contactAttributes,
      }),
    [persona, contactAttributes]
  )

  const methods = useForm<PersonaForm>({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  })

  const {
    formState,
    control,
    watch,
    reset,
    trigger,
    setValue,
    register,
    unregister,
    handleSubmit,
  } = methods

  const { isValid } = formState

  const mode = watch('mode')
  const attributes = watch('attributes')

  const { remove, append, update, move } = useFieldArray({
    control,
    name: 'attributes',
  })

  const hasAIMode = mode === PROMPT_MODES.AI
  const isDefaultStateUpdated =
    JSON.stringify(attributes) !== JSON.stringify(defaultValues.attributes)
  const _hasAttributesChanges = useRef<boolean>(false)

  useEffect(() => {
    _hasAttributesChanges.current = isDefaultStateUpdated
  }, [isDefaultStateUpdated])

  const onSubmit = (data: PersonaForm) => {
    const attributesPayload = buildAttributesPayload(data.attributes)

    handleSavePersona.mutate(
      {
        id: persona?.id,
        name: data.name || QUALIFICATION_DEFAULT_NAME,
        prompt: data.prompt || '',
        attributes: attributesPayload,
        qualifiedCQL,
        unqualifiedCQL,
        minCriteriaMet,
      },
      {
        onSuccess: () => setOpenConfirmation(false),
      }
    )

    trackLQ(AILQTrackAction.SAVE_ATTRIBUTES, {
      mode: data.mode,
      attributes: attributesPayload,
    })

    reset({
      ...data,
      foundAIAttributes: hasAIMode ? data.attributes : undefined,
      hasAcknowledge: false,
    })
  }

  const handleChangesInAttributes = () => setOpenConfirmation(true)

  const handleNoChangesInAttributes = () => handleSubmit(onSubmit)()

  const handleContinue = () => handleSubmit(onSubmit)()

  const handleSave = async () => {
    if (!isValid) {
      toast({
        message: 'Please fix the errors in the form',
        variant: 'error',
      })
      return
    }

    if (!(await trigger())) return

    if (isDefaultStateUpdated) handleChangesInAttributes()
    else handleNoChangesInAttributes()
  }

  const handleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    const anchorTag = (target && target.closest('a')) || null
    if (
      personaContainer.current &&
      !personaContainer.current.contains(target) &&
      anchorTag instanceof HTMLAnchorElement &&
      anchorTag.href
    ) {
      if (_hasAttributesChanges.current) {
        e.preventDefault()
        e.stopPropagation()
        setLeaveModalConfig({
          open: true,
          nextHref: anchorTag.href,
        })
      }
    }
  }

  // Sort attributes list by dragging an item based on the required field
  // Required fields will always appear at the top
  const sortAttrsByRequiredOnDrag = (
    sourceIndex: number,
    destinationIndex: number
  ) => {
    const sourceItem = attributes[sourceIndex]

    // Find the boundary between required and unrequired items
    //If the attributes list is all set to required, findIndex will return -1
    const findIndexCheck = attributes.findIndex((attr) => !attr.required)
    const lastRequiredIndex = findIndexCheck - 1

    // Moving a REQUIRED item
    if (sourceItem.required) {
      //If findIndexCheck is -1, all items are required, so no need to limit movement
      if (findIndexCheck !== -1 && destinationIndex > lastRequiredIndex) {
        // Trying to move into unrequired section - place at bottom of required
        toast({
          message: 'Required rules must remain above optional rules.',
          variant: 'error',
          options: {
            duration: 4000,
          },
        })
        move(sourceIndex, lastRequiredIndex)
        return
      }

      // Moving a REQUIRED item within required section
      move(sourceIndex, destinationIndex)
      return
    }

    // Moving an UNREQUIRED item
    if (destinationIndex <= lastRequiredIndex) {
      // Trying to move an UNREQUIRED item into required section - place at top of unrequired
      move(sourceIndex, lastRequiredIndex + 1)
      toast({
        message: 'Optional rules must remain below required rules.',
        variant: 'error',
        options: {
          duration: 4000,
        },
      })
      return
    }

    // Moving within unrequired section
    move(sourceIndex, destinationIndex)
  }

  // Sort attributes by click on required field checkbox
  const sortAttrsByRequiredOnClick = () => {
    const sortedFields = [...attributes].sort((a, b) => {
      if (a.required && !b.required) return -1
      if (!a.required && b.required) return 1
      return 0
    })

    sortedFields.forEach((field, index) => update(index, field))
  }

  const handleClickRequiredAttribute = () => sortAttrsByRequiredOnClick()

  const handleDragAttribute = (result: DropResult) => {
    if (result.destination) {
      sortAttrsByRequiredOnDrag(result.source.index, result.destination.index)
    }
  }

  const handleAddAttribute = () => {
    append({
      name: null,
      operator: null,
      value: '',
      required: false,
    })
  }

  const handleRemoveAttribute = (idx: number) => remove(idx)

  const minRequiredFields = attributes.filter((field) => field.required).length

  // Add an empty, editable row if there are no attributes
  useEffect(() => {
    if (!attributes.length) {
      handleAddAttribute()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setValue('hasAcknowledge', false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(attributes)])

  useEffect(() => {
    register('foundAIAttributes')
    document.addEventListener('click', handleLinkClick)
    return () => {
      unregister('foundAIAttributes')
      document.removeEventListener('click', handleLinkClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (handleSavePersona.isLoading) {
      return
    }

    const updatedMinCriteriaMet = attributes.filter(
      (field) => field.required
    ).length

    if (
      !persona?.minCriteriaMet ||
      persona.minCriteriaMet >= attributes.length
    ) {
      const validAttributesCount = attributes.filter(
        (field) => field.name && field.operator
      ).length

      setMinCriteriaMet(validAttributesCount || 1)
      return
    }

    setMinCriteriaMet(updatedMinCriteriaMet || 1)
  }, [persona, attributes, handleSavePersona.isLoading])

  return (
    <FormProvider {...methods}>
      <Flex
        ref={personaContainer}
        as="form"
        justifyContent="center"
        alignItems="center"
        fullHeight
        fullWidth
        direction="column"
        gap={3}
        style={{ padding: '$4' }}
      >
        {/* Qual rules */}
        <Card
          css={{
            width: 'calc(100% - ($2 * 2))',
            maxWidth: '1024px',
            padding: '$3 0 0',
            gap: '$3',
            margin: '0 $2',
          }}
        >
          <Card.Header css={{ padding: '0 $2' }}>
            <Flex direction="row" justifyContent="space-between" width={'100%'}>
              <Header variant="h3">Qualification rules</Header>
              <Flex>
                <EducationPopover
                  show={showAssistance}
                  onOpenChange={() => setShowAssistance((prev) => !prev)}
                />
                <Button variant="secondary" onClick={handleAddAttribute}>
                  Add attribute
                </Button>
              </Flex>
            </Flex>
            <Text variant="subtitle2">
              This definition will be used to power the lead qualification skill
              across all playbooks. The order of your rules determines the
              sequence in which the bot asks questions. Required questions will
              always appear at the top.
            </Text>
          </Card.Header>
          <Card.Content>
            <Attributes
              fields={attributes}
              isDragAndDropEnable={true}
              contactAttributes={contactAttributes}
              handleRemoveAttribute={handleRemoveAttribute}
              handleClickRequiredAttribute={handleClickRequiredAttribute}
              handleDragAttribute={handleDragAttribute}
            />
          </Card.Content>
        </Card>
        {/* Qual floor */}
        <Card
          css={{
            width: 'calc(100% - ($2 * 2))',
            maxWidth: '1024px',
            padding: '$3 $2',
            gap: '$3',
            margin: '0 $2',
          }}
        >
          <Card.Header>
            <Header variant="h3">Qualification floor</Header>
            <Text variant="subtitle2">
              The minimum threshold must be greater than or equal to the number
              of required criteria.
            </Text>
          </Card.Header>
          <Card.Content>
            <Text variant="body" css={{ color: '$gray12' }}>
              Minimum number of rules that must be met
            </Text>
            <InputNumber
              label=""
              value={minCriteriaMet}
              onChange={(value) => setMinCriteriaMet(value)}
              min={minRequiredFields}
              max={attributes?.length || 1}
            />
          </Card.Content>
        </Card>
        {/* CQL scores */}
        <Card
          css={{
            width: 'calc(100% - ($2 * 2))',
            maxWidth: '1024px',
            padding: '$3 $2',
            gap: '$3',
            margin: '0 $2',
          }}
        >
          <Card.Header>
            <Header variant="h3">Assign CQL scores</Header>
            <Text variant="subtitle2">
              Depending on the visitor’s responses, automatically assign CQL
              scores to mark them as qualified or unqualified.
            </Text>
          </Card.Header>
          <Card.Content>
            <Flex direction="column">
              <Text variant="body" css={{ color: '$gray12' }}>
                Qualified visitors
              </Text>
              <CQLRatingGroup
                onRatingClick={(rating) => setQualifiedCQL(rating)}
                rating={qualifiedCQL}
              />
            </Flex>
            <Flex direction="column" css={{ marginTop: '$3' }}>
              <Text variant="body" css={{ color: '$gray12' }}>
                Unqualified visitors
              </Text>
              <CQLRatingGroup
                onRatingClick={(rating) => setUnqualifiedCQL(rating)}
                rating={unqualifiedCQL}
              />
            </Flex>
            <Flex justifyContent="flex-end">
              <Button
                onClick={handleSave}
                loading={handleSavePersona.isLoading}
              >
                Save
              </Button>
            </Flex>
          </Card.Content>
        </Card>
      </Flex>

      <ConfirmationModal
        aiMode={mode}
        isLoading={handleSavePersona.isLoading}
        isOpen={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleContinue={handleContinue}
      />

      <LeavingModal
        open={leaveModalConfig.open}
        handleExit={() => {
          window.location.href = new URL(leaveModalConfig.nextHref).href
        }}
        handleContinue={() => {
          setLeaveModalConfig({ nextHref: '', open: false })
        }}
        handleSave={() => {
          handleContinue()
          setLeaveModalConfig({ nextHref: '', open: false })
        }}
      />
    </FormProvider>
  )
}

export default PersonaCard
